import * as THREE from 'three'
import GUI from 'lil-gui'
import {getDevices} from './devices.js';
import { FontLoader } from 'three/examples/jsm/loaders/FontLoader.js'
import { LineSegments2 } from 'three/examples/jsm/lines/LineSegments2.js'
import { LineSegmentsGeometry } from 'three/examples/jsm/lines/LineSegmentsGeometry.js'
import { LineMaterial } from 'three/examples/jsm/lines/LineMaterial.js'

const gui = new GUI()
gui.close()
const canvas = document.querySelector('canvas.webgl')
const deviceSlider  = document.getElementById('device-slider')
const deviceCounter = document.getElementById('device-counter')
const scene = new THREE.Scene()
scene.background = new THREE.Color(0x0f172a)

const sizes = { width: window.innerWidth, height: window.innerHeight }
window.addEventListener('resize', () => {
    sizes.width = window.innerWidth
    sizes.height = window.innerHeight
    camera.aspect = sizes.width / sizes.height
    camera.updateProjectionMatrix()
    renderer.setSize(sizes.width, sizes.height)
    renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
    matGuide.resolution.set(sizes.width, sizes.height)
    matWidth.resolution.set(sizes.width, sizes.height)
    matHeight.resolution.set(sizes.width, sizes.height)
})

// Camera travels along Z. X is fixed, Y auto-tracks current device height.
const CAMERA_X = 800
const LOOK_AHEAD = 4000
const CAMERA_Y_OVERHEAD = 2000  // units above device top the camera hovers
let cameraTargetY  = 3500
let cameraCurrentY = 3500
let cameraTargetZ  = -1000
let cameraCurrentZ = -1000
let minCameraZ = -1000
let maxCameraZ = 10000
const lerpFactor = 0.08
let deviceHeightsByZ = []  // [{z, height}] – populated in loadDevices
let deviceMeshes = []      // [{mesh, w, h, z}] – populated in loadDevices
let currentDeviceIndex = 0

const BOX_DEPTH = 60

function findNearestDevice(targetZ) {
    let nearest = deviceHeightsByZ[0]
    let minDist = Math.abs(nearest.z - targetZ)
    for (const dp of deviceHeightsByZ) {
        const dist = Math.abs(dp.z - targetZ)
        if (dist < minDist) { minDist = dist; nearest = dp }
    }
    return nearest
}

const camera = new THREE.PerspectiveCamera(75, sizes.width / sizes.height, 100, 500000)
camera.position.set(CAMERA_X, cameraCurrentY, cameraCurrentZ)
scene.add(camera)

// Scroll → one device at a time
canvas.addEventListener('wheel', (e) => {
    e.preventDefault()
    if (deviceMeshes.length === 0) return
    const dir = e.deltaY > 0 ? 1 : -1
    currentDeviceIndex = Math.max(0, Math.min(deviceMeshes.length - 1, currentDeviceIndex + dir))
    cameraTargetZ = deviceMeshes[currentDeviceIndex].z + LOOK_AHEAD * 0.5
    updateHighlight()
}, { passive: false })


const renderer = new THREE.WebGLRenderer({ canvas })
renderer.setSize(sizes.width, sizes.height)
renderer.setPixelRatio(Math.min(window.devicePixelRatio, 2))
renderer.shadowMap.enabled = true
renderer.shadowMap.type = THREE.PCFSoftShadowMap

const ambientLight = new THREE.AmbientLight(0xffffff, 0.8)
scene.add(ambientLight)

const directionalLight = new THREE.DirectionalLight(0xffffff, 1.2)
directionalLight.castShadow = true
directionalLight.shadow.mapSize.set(2048, 2048)
directionalLight.shadow.camera.near = 1
directionalLight.shadow.camera.far = 500000
directionalLight.shadow.camera.left  = -3000
directionalLight.shadow.camera.right =  3000
directionalLight.shadow.camera.top   =  6000
directionalLight.shadow.camera.bottom = -500
scene.add(directionalLight)

// Long floor along Z
const floorGeo = new THREE.PlaneGeometry(8000, 600000)
const floorMat = new THREE.MeshStandardMaterial({ color: 0x0d1b2a, side: THREE.DoubleSide })
const floor = new THREE.Mesh(floorGeo, floorMat)
floor.rotation.x = -Math.PI * 0.5
floor.position.set(CAMERA_X, -2, 300000)
floor.receiveShadow = true
scene.add(floor)

// Highlight outline — persistent, repositioned per selected device
const highlightOutline = (() => {
    const geo = new THREE.EdgesGeometry(new THREE.BoxGeometry(1, 1, 1))
    const mat = new THREE.LineBasicMaterial({ color: 0xffff00 })
    const ls  = new THREE.LineSegments(geo, mat)
    ls.frustumCulled = false
    ls.visible = false
    scene.add(ls)
    return ls
})()

const geometry = new THREE.BoxGeometry(1, 1, 1)

const brandMaterials = {
    'apple-phone':    new THREE.MeshStandardMaterial({ color: 0x1d4ed8 }),
    'apple-tablet':   new THREE.MeshStandardMaterial({ color: 0x60a5fa }),
    'samsung-phone':  new THREE.MeshStandardMaterial({ color: 0x0f766e }),
    'samsung-tablet': new THREE.MeshStandardMaterial({ color: 0x2dd4bf }),
    'xiaomi-phone':   new THREE.MeshStandardMaterial({ color: 0x9a3412 }),
    'xiaomi-tablet':  new THREE.MeshStandardMaterial({ color: 0xf97316 }),
    'default':        new THREE.MeshStandardMaterial({ color: 0x1e3a5f }),
}
function getDeviceMaterial(device) {
    const brand = (device.brand || '').toLowerCase()
    const type  = (device.type  || 'phone').toLowerCase()
    return brandMaterials[brand + '-' + type] || brandMaterials['default']
}

function makeTestPatternTexture(deviceWidth, deviceHeight) {
    const CELL   = 100
    const COLORS = ['#E53E3E', '#38A169', '#3182CE', '#D69E2E', '#00B5D8', '#805AD5']
    const LABELS = ['A', 'B', 'C', 'D', 'E', 'F']
    const N     = COLORS.length           // 6
    const tileW = CELL * N                 // 600
    const tileH = CELL * N                 // 600 — one full cycle of row offsets
    const canvas = document.createElement('canvas')
    canvas.width  = tileW
    canvas.height = tileH
    const ctx = canvas.getContext('2d')
    for (let row = 0; row < N; row++) {
        const cy     = (N - 1 - row) * CELL  // row=0 → canvas bottom → screen bottom after flipY
        const rowNum = row + 1               // row=0 (screen bottom) → 1, row=N-1 (screen top) → N
        for (let col = 0; col < N; col++) {
            const idx   = (col - row + N) % N  // bottom row starts A, each row up shifts back 1
            const label = LABELS[idx] + rowNum
            ctx.fillStyle = COLORS[idx]
            ctx.fillRect(col * CELL, cy, CELL, CELL)
            ctx.strokeStyle = 'rgba(0,0,0,0.25)'
            ctx.lineWidth = 2
            ctx.strokeRect(col * CELL + 1, cy + 1, CELL - 2, CELL - 2)
            ctx.fillStyle = '#ffffff'
            ctx.font = 'bold 44px sans-serif'
            ctx.textAlign = 'center'
            ctx.textBaseline = 'middle'
            ctx.fillText(label, col * CELL + CELL / 2, cy + CELL / 2)
        }
    }
    const tex = new THREE.CanvasTexture(canvas)
    tex.wrapS = THREE.RepeatWrapping
    tex.wrapT = THREE.RepeatWrapping
    tex.repeat.set(deviceWidth / tileW, deviceHeight / tileH)
    return tex
}

const matText       = new THREE.MeshBasicMaterial({ color: 0xe2e8f0, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 })
const matTextHL     = new THREE.MeshBasicMaterial({ color: 0xffff00, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 })
const matTextDim    = new THREE.MeshBasicMaterial({ color: 0xe2e8f0, transparent: true, opacity: 0.25, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 })
const matTextWidth  = new THREE.MeshBasicMaterial({ color: 0xfbbf24, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 })
const matTextHeight = new THREE.MeshBasicMaterial({ color: 0xa78bfa, side: THREE.DoubleSide, polygonOffset: true, polygonOffsetFactor: -1, polygonOffsetUnits: -1 })
const matAxis  = new THREE.LineBasicMaterial({ color: 0x475569 })
const matGuide = new LineMaterial({
    color: 0xffffff,
    linewidth: 3,
    worldUnits: false,
    resolution: new THREE.Vector2(sizes.width, sizes.height)
})
const matWidth = new LineMaterial({
    color: 0x4ade80,
    linewidth: 3,
    worldUnits: false,
    resolution: new THREE.Vector2(sizes.width, sizes.height)
})
const matHeight = new LineMaterial({
    color: 0xa78bfa,
    linewidth: 3,
    worldUnits: false,
    resolution: new THREE.Vector2(sizes.width, sizes.height)
})

let font = null

let deviceSceneObjects = []
function addDeviceObject(obj) { scene.add(obj); deviceSceneObjects.push(obj) }
function clearDeviceObjects() {
    for (const obj of deviceSceneObjects) {
        scene.remove(obj)
        if (obj.geometry) obj.geometry.dispose()
        if (obj.userData && obj.userData.hlMat)   obj.userData.hlMat.dispose()
        if (obj.userData && obj.userData.edgesMat) obj.userData.edgesMat.dispose()
        if (obj.userData && obj.userData.testTex) obj.userData.testTex.dispose()
        if (obj.userData && obj.userData.testMat) obj.userData.testMat.dispose()
    }
    deviceSceneObjects = []
    deviceMeshes = []
}

function updateHighlight() {
    deviceMeshes.forEach(({ mesh }, i) => {
        const selected = i === currentDeviceIndex
        const before   = i < currentDeviceIndex
        mesh.visible  = !before
        mesh.material = selected ? mesh.userData.hlMat : mesh.userData.baseMat
        if (mesh.userData.edgesMesh) mesh.userData.edgesMesh.visible = before
        if (mesh.userData.testPlane) mesh.userData.testPlane.visible = !before
        if (mesh.userData.infoMesh) mesh.userData.infoMesh.material = selected ? matTextHL : before ? matTextDim : matText
    })
    deviceSlider.value       = currentDeviceIndex
    deviceCounter.textContent = `${currentDeviceIndex + 1} / ${deviceMeshes.length}`
    if (deviceMeshes.length === 0) { highlightOutline.visible = false; return }
    const { w, h, z } = deviceMeshes[currentDeviceIndex]
    const pad = 30
    highlightOutline.scale.set(w + pad * 2, h + pad * 2, BOX_DEPTH + pad * 2)
    highlightOutline.position.set(w * 0.5, h * 0.5, z + BOX_DEPTH * 0.5)
    highlightOutline.visible = true
}

// Single axis group — follows camera in Z, sits at world X/Y = 0
const axisGroup = new THREE.Group()
scene.add(axisGroup)

function buildAxes(maxW) {
    while (axisGroup.children.length) {
        const c = axisGroup.children[0]
        axisGroup.remove(c)
        if (c.geometry) c.geometry.dispose()
    }

    // X axis line at Y = -100 (just below floor level)
    axisGroup.add(new THREE.Line(
        new THREE.BufferGeometry().setFromPoints([
            new THREE.Vector3(0,          -100, 0),
            new THREE.Vector3(maxW + 300, -100, 0)
        ]), matAxis
    ))

    // X axis ticks every 200 px, label every 400
    for (let w = 0; w <= maxW + 100; w += 200) {
        axisGroup.add(new THREE.Line(
            new THREE.BufferGeometry().setFromPoints([
                new THREE.Vector3(w, -60,  0),
                new THREE.Vector3(w, -140, 0)
            ]), matAxis
        ))
        if (w % 400 === 0) {
            const shapes = font.generateShapes(String(w), 38)
            const geo    = new THREE.ShapeGeometry(shapes)
            geo.computeBoundingBox()
            const lw  = geo.boundingBox.max.x - geo.boundingBox.min.x
            const mesh = new THREE.Mesh(geo, matText)
            mesh.position.set(w - lw * 0.5, -210, 0)
            axisGroup.add(mesh)
        }
    }
}

// Data
const allDevices = getDevices()
const KNOWN_BRANDS = ['apple', 'samsung', 'xiaomi']
const activeBrands = new Set(KNOWN_BRANDS)
const KNOWN_TYPES  = ['phone', 'tablet']
const activeTypes  = new Set(KNOWN_TYPES)

let currentSortMode = 'pixels'
const DEVICE_Z_GAP = BOX_DEPTH  // devices touch — zero gap between front and back faces
const Z_START      = 3000

function getSortValue(d) {
    const w = parseFloat(d.pixels.width)
    const h = parseFloat(d.pixels.height)
    if (currentSortMode === 'height') return h
    if (currentSortMode === 'width')  return w
    if (currentSortMode === 'pixels') return w * h
    if (currentSortMode === 'year')   return parseInt(d.year || 9999)
    return w / h
}

function loadDevices() {
    clearDeviceObjects()

    const devices = allDevices.filter(d => {
        if (!d.year || isNaN(parseInt(d.year))) return false
        const brand = (d.brand || '').toLowerCase()
        const type  = (d.type  || 'phone').toLowerCase()
        if (KNOWN_BRANDS.includes(brand) && !activeBrands.has(brand)) return false
        if (KNOWN_TYPES.includes(type)   && !activeTypes.has(type))   return false
        return true
    })

    devices.sort((a, b) => {
        const sd = getSortValue(a) - getSortValue(b)
        if (Math.abs(sd) > 0.0001) return sd
        // tie-break: year sort → pixels ascending; all others → year ascending
        if (currentSortMode === 'year') {
            const pa = parseFloat(a.pixels.width) * parseFloat(a.pixels.height)
            const pb = parseFloat(b.pixels.width) * parseFloat(b.pixels.height)
            return pa - pb
        }
        return parseInt(a.year || 9999) - parseInt(b.year || 9999)
    })

    const maxH = Math.max(...devices.map(d => parseFloat(d.pixels.height)))
    const maxW = Math.max(...devices.map(d => parseFloat(d.pixels.width)))

    buildAxes(maxW)

    const lastZ = Z_START + (devices.length - 1) * DEVICE_Z_GAP
    // Camera at high Z looking toward low Z; oldest devices at high Z (closest to start)
    minCameraZ  = Z_START + LOOK_AHEAD * 0.5   // near newest devices
    maxCameraZ  = lastZ   + LOOK_AHEAD * 0.5   // near oldest devices (start here)
    cameraTargetZ  = maxCameraZ
    cameraCurrentZ = maxCameraZ

    // Build height lookup for auto-tracking
    deviceHeightsByZ = devices.map((device, index) => ({
        z: lastZ - index * DEVICE_Z_GAP,
        height: parseFloat(device.pixels.height)
    }))
    // Reset device selection to the first (largest/oldest) device
    currentDeviceIndex = 0
    cameraTargetZ  = maxCameraZ
    cameraCurrentZ = maxCameraZ

    // Initialise camera Y to hover over whichever device the camera looks at on load
    const initDevice = findNearestDevice(maxCameraZ - LOOK_AHEAD)
    cameraTargetY  = initDevice.height + CAMERA_Y_OVERHEAD
    cameraCurrentY = cameraTargetY

    const RAIL_X = 0
    const textX  = maxW + 200

    let prevW = null
    let prevH = null

    // Flat position arrays for LineSegments2 (pairs of x,y,z per segment endpoint)
    const guidePts  = [
        // Main rail along Z
        RAIL_X, 20, Z_START,
        RAIL_X, 20, lastZ,
    ]
    const widthPts  = []  // amber — width markers along Z
    const heightPts = []  // cyan  — height markers along Z + vertical connectors

    devices.forEach((device, index) => {
        const w  = parseFloat(device.pixels.width)
        const h  = parseFloat(device.pixels.height)
        const z  = lastZ - index * DEVICE_Z_GAP   // oldest at lastZ (high Z, closest to camera)
        const cx = w * 0.5
        const cy = h * 0.5

        // Device box — clone a highlight material variant per device
        const baseMat = getDeviceMaterial(device)
        const hlMat   = baseMat.clone()
        hlMat.emissive.set(0xaaaa00)
        hlMat.emissiveIntensity = 0.7
        const cube = new THREE.Mesh(geometry, baseMat)
        cube.castShadow    = true
        cube.receiveShadow = true
        cube.scale.set(w, h, BOX_DEPTH)
        cube.position.set(cx, cy, z + BOX_DEPTH * 0.5)
        cube.userData.baseMat = baseMat
        cube.userData.hlMat   = hlMat
        deviceMeshes.push({ mesh: cube, w, h, z })
        addDeviceObject(cube)

        // Edge-only wireframe shown in place of the solid box for "before" devices
        const edgesMat  = new THREE.LineBasicMaterial({ color: baseMat.color.clone() })
        const edgesMesh = new THREE.LineSegments(new THREE.EdgesGeometry(geometry), edgesMat)
        edgesMesh.scale.copy(cube.scale)
        edgesMesh.position.copy(cube.position)
        edgesMesh.visible = false
        cube.userData.edgesMesh = edgesMesh
        cube.userData.edgesMat  = edgesMat
        addDeviceObject(edgesMesh)

        // Test-pattern plane overlaid on the front face (+Z)
        const testTex   = makeTestPatternTexture(w, h)
        const testMat   = new THREE.MeshBasicMaterial({ map: testTex, side: THREE.DoubleSide })
        const testPlane = new THREE.Mesh(new THREE.PlaneGeometry(w, h), testMat)
        testPlane.position.set(cx, cy, z + BOX_DEPTH + 0.5)
        testPlane.userData.testTex = testTex
        testPlane.userData.testMat = testMat
        cube.userData.testMat   = testMat
        cube.userData.testPlane = testPlane
        addDeviceObject(testPlane)

        // Horizontal guide: from rail to text column
        guidePts.push(RAIL_X, 20, z, textX, 20, z)

        // Width marker: Z-direction line at X=device.width, from current guide to next guide
        widthPts.push(w, 20, z, w, 20, z - DEVICE_Z_GAP)

        // Width label: only when width changed from previous device
        if (Math.round(w) !== prevW) {
            const widthText  = Math.round(w) + ' px'
            const widthGeo   = new THREE.ShapeGeometry(font.generateShapes(widthText, 50))
            widthGeo.computeBoundingBox()
            const wTextW     = widthGeo.boundingBox.max.x - widthGeo.boundingBox.min.x
            const wCenterZ   = (widthGeo.boundingBox.max.y + widthGeo.boundingBox.min.y) * 0.5
            const widthMesh  = new THREE.Mesh(widthGeo, matTextWidth)
            widthMesh.rotation.x = -Math.PI / 2
            widthMesh.position.set(cx - wTextW * 0.5, h + 5, z + BOX_DEPTH * 0.5 + wCenterZ)
            addDeviceObject(widthMesh)
        }

        // Height marker: Z-direction line at top-left corner (X=0, Y=h), current to next guide
        heightPts.push(0, h, z, 0, h, z - DEVICE_Z_GAP)

        // Vertical connector: from ground rail (Y=20) to height rail (Y=h) at device's Z
        heightPts.push(0, 20, z, 0, h, z)

        // Height label: only when height changed from previous device
        if (Math.round(h) !== prevH) {
            const heightText = Math.round(h) + ' px'
            const heightGeo  = new THREE.ShapeGeometry(font.generateShapes(heightText, 80))
            heightGeo.computeBoundingBox()
            const hTextW     = heightGeo.boundingBox.max.x - heightGeo.boundingBox.min.x
            const hTextH     = heightGeo.boundingBox.max.y - heightGeo.boundingBox.min.y
            const heightMesh = new THREE.Mesh(heightGeo, matTextHeight)
            heightMesh.rotation.x = -Math.PI / 2
            heightMesh.position.set(-hTextW - 40, h + 5, z + BOX_DEPTH * 0.5 + hTextH * 0.5)
            addDeviceObject(heightMesh)
        }

        prevW = Math.round(w)
        prevH = Math.round(h)

        // Single info line: index, name, resolution, year — flat on ground at device Z
        const infoText = `${index + 1}. ${device.name}  ${Math.round(w)}x${Math.round(h)}  ${device.year || ''}`
        const infoGeo  = new THREE.ShapeGeometry(font.generateShapes(infoText, 50))
        const infoMesh = new THREE.Mesh(infoGeo, matText)
        infoMesh.rotation.x = -Math.PI / 2
        infoMesh.position.set(textX, 20, z + BOX_DEPTH + 20)
        cube.userData.infoMesh = infoMesh
        addDeviceObject(infoMesh)
    })

    // Build thick guide lines — three separate passes for independent colours
    const guideGeo = new LineSegmentsGeometry()
    guideGeo.setPositions(guidePts)
    const guideLines = new LineSegments2(guideGeo, matGuide)
    guideLines.frustumCulled = false
    addDeviceObject(guideLines)

    const widthGeo2 = new LineSegmentsGeometry()
    widthGeo2.setPositions(widthPts)
    const widthLines = new LineSegments2(widthGeo2, matWidth)
    widthLines.frustumCulled = false
    addDeviceObject(widthLines)

    const heightGeo2 = new LineSegmentsGeometry()
    heightGeo2.setPositions(heightPts)
    const heightLines = new LineSegments2(heightGeo2, matHeight)
    heightLines.frustumCulled = false
    addDeviceObject(heightLines)

    deviceSlider.max = devices.length - 1
    updateHighlight()
}

function loaded(inFont) {
    font = inFont
    loadDevices()
}
new FontLoader().load('/fonts/helvetiker_regular.typeface.json', loaded)

document.getElementById('sort-select').addEventListener('change', (e) => {
    currentSortMode = e.target.value
    loadDevices()
})

deviceSlider.addEventListener('input', () => {
    currentDeviceIndex = parseInt(deviceSlider.value)
    cameraTargetZ = deviceMeshes[currentDeviceIndex].z + LOOK_AHEAD * 0.5
    updateHighlight()
})

for (const brand of KNOWN_BRANDS) {
    document.getElementById(`filter-${brand}`).addEventListener('change', (e) => {
        if (e.target.checked) activeBrands.add(brand)
        else activeBrands.delete(brand)
        loadDevices()
    })
}

for (const type of KNOWN_TYPES) {
    document.getElementById(`filter-${type}`).addEventListener('change', (e) => {
        if (e.target.checked) activeTypes.add(type)
        else activeTypes.delete(type)
        loadDevices()
    })
}

const tick = () => {
    // Auto-track camera Y to whichever device the camera is looking at
    if (deviceHeightsByZ.length > 0) {
        const nearest = findNearestDevice(cameraCurrentZ - LOOK_AHEAD)
        cameraTargetY = nearest.height + CAMERA_Y_OVERHEAD
    }

    cameraCurrentY += (cameraTargetY - cameraCurrentY) * lerpFactor
    cameraCurrentZ += (cameraTargetZ - cameraCurrentZ) * lerpFactor

    camera.position.set(CAMERA_X, cameraCurrentY, cameraCurrentZ)
    camera.lookAt(CAMERA_X, cameraCurrentY - 2800, cameraCurrentZ - 1200)

    // Axes float in front of camera (toward lower Z)
    axisGroup.position.set(0, 0, cameraCurrentZ - LOOK_AHEAD * 0.65)

    // Light tracks camera
    directionalLight.position.set(
        CAMERA_X + 1500,
        cameraCurrentY + 5000,
        cameraCurrentZ - 2000
    )
    directionalLight.target.position.set(CAMERA_X, cameraCurrentY, cameraCurrentZ - LOOK_AHEAD)
    directionalLight.target.updateMatrixWorld()

    renderer.render(scene, camera)
    window.requestAnimationFrame(tick)
}

tick()
